/**
 * IndexedDB utilities for storing and retrieving OHLC (1m base) chart data.
 * Pure cache layer — no Zustand coupling; callers pass data explicitly.
 */

import type { OHLCData } from "./types";

const DB_NAME = "VybeTraderDB";
const DB_VERSION = 4;
const STORE_NAME = "ohlcData";
/** One record per UTC day of 1m bars (written from ohlc-idb-cache.worker). */
export const OHLC_BY_DAY_STORE = "ohlcByDay";

/** Persisted row shape (extra fields are backward-compatible with v2). */
export interface OHLCIndexedRecord {
  key: string;
  data: OHLCData[];
  lastUpdated: string;
  count: number;
  rangeFromMs: number;
  rangeToMs: number;
}

// Generate account-specific key for IndexedDB
export const generateAccountSymbolKey = (
  companyName: string,
  serverName: string,
  symbolCurrency: string
): string => {
  return `${companyName}_${serverName}_${symbolCurrency}`;
};

// IndexedDB wrapper class
class OHLCIndexDB {
  private db: IDBDatabase | null = null;

  // Initialize database
  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error("[IndexDB] Failed to open database:", request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (event.oldVersion < 1) {
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            db.createObjectStore(STORE_NAME, {
              keyPath: "key",
            });
          }
        } else if (event.oldVersion === 1) {
          if (db.objectStoreNames.contains(STORE_NAME)) {
            db.deleteObjectStore(STORE_NAME);
          }
          db.createObjectStore(STORE_NAME, {
            keyPath: "key",
          });
        }
        if (event.oldVersion < 4 && !db.objectStoreNames.contains(OHLC_BY_DAY_STORE)) {
          db.createObjectStore(OHLC_BY_DAY_STORE, { keyPath: "key" });
        }
      };
    });
  }

  // Save OHLC data for a symbol with account-specific key
  async saveOHLCData(accountKey: string, data: OHLCData[]): Promise<void> {
    if (!this.db) {
      throw new Error("Database not initialized");
    }

    const sorted =
      data.length > 0
        ? [...data].sort((a, b) => a.timestamp - b.timestamp)
        : [];
    const rangeFromMs = sorted[0]?.timestamp ?? 0;
    const rangeToMs = sorted[sorted.length - 1]?.timestamp ?? 0;

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], "readwrite");
      const objectStore = transaction.objectStore(STORE_NAME);

      const record: OHLCIndexedRecord = {
        key: accountKey,
        data: sorted,
        lastUpdated: new Date().toISOString(),
        count: sorted.length,
        rangeFromMs,
        rangeToMs,
      };

      const request = objectStore.put(record);

      request.onerror = () => {
        console.error(
          `[IndexDB] Failed to save data for ${accountKey}:`,
          request.error
        );
        reject(request.error);
      };

      request.onsuccess = () => {
        resolve();
      };
    });
  }

  // Load OHLC data for a symbol with account-specific key
  async loadOHLCData(accountKey: string): Promise<OHLCData[] | null> {
    if (!this.db) {
      throw new Error("Database not initialized");
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], "readonly");
      const objectStore = transaction.objectStore(STORE_NAME);
      const request = objectStore.get(accountKey);

      request.onerror = () => {
        console.error(
          `[IndexDB] Failed to load data for ${accountKey}:`,
          request.error
        );
        reject(request.error);
      };

      request.onsuccess = () => {
        const result = request.result as OHLCIndexedRecord | undefined;
        if (result?.data?.length) {
          resolve(result.data);
        } else {
          resolve(null);
        }
      };
    });
  }

  // Delete data for a symbol with account-specific key
  async deleteOHLCData(accountKey: string): Promise<void> {
    if (!this.db) {
      throw new Error("Database not initialized");
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], "readwrite");
      const objectStore = transaction.objectStore(STORE_NAME);
      const request = objectStore.delete(accountKey);

      request.onerror = () => {
        console.error(
          `[IndexDB] Failed to delete data for ${accountKey}:`,
          request.error
        );
        reject(request.error);
      };

      request.onsuccess = () => {
        resolve();
      };
    });
  }

  // Clear all data
  async clearAll(): Promise<void> {
    if (!this.db) {
      throw new Error("Database not initialized");
    }

    const stores = [STORE_NAME];
    if (this.db.objectStoreNames.contains(OHLC_BY_DAY_STORE)) {
      stores.push(OHLC_BY_DAY_STORE);
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(stores, "readwrite");
      transaction.onerror = () => {
        console.error("[IndexDB] Failed to clear all data:", transaction.error);
        reject(transaction.error);
      };
      transaction.oncomplete = () => resolve();
      for (const name of stores) {
        transaction.objectStore(name).clear();
      }
    });
  }

  // Clear data for specific account (all symbols)
  async clearAccountData(
    companyName: string,
    serverName: string
  ): Promise<void> {
    if (!this.db) {
      throw new Error("Database not initialized");
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], "readwrite");
      const objectStore = transaction.objectStore(STORE_NAME);
      const request = objectStore.openCursor();

      const keysToDelete: string[] = [];
      const accountPrefix = `${companyName}_${serverName}_`;

      request.onerror = () => {
        console.error("[IndexDB] Failed to clear account data:", request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          if (cursor.key.toString().startsWith(accountPrefix)) {
            keysToDelete.push(cursor.key.toString());
          }
          cursor.continue();
        } else {
          let deleteCount = 0;
          if (keysToDelete.length === 0) {
            resolve();
            return;
          }
          keysToDelete.forEach((key) => {
            const deleteRequest = objectStore.delete(key);
            deleteRequest.onsuccess = () => {
              deleteCount++;
              if (deleteCount === keysToDelete.length) {
                resolve();
              }
            };
            deleteRequest.onerror = () => reject(deleteRequest.error);
          });
        }
      };
    });
  }
}

// Singleton instance
const ohlcIndexDB = new OHLCIndexDB();

// Enhanced IndexedDB class with initialization method
class EnhancedOHLCIndexDB extends OHLCIndexDB {
  private initialized = false;

  async init(): Promise<void> {
    if (!this.initialized) {
      await super.init();
      this.initialized = true;
    }
  }

  async loadOHLCData(accountKey: string): Promise<OHLCData[] | null> {
    await this.init();
    return super.loadOHLCData(accountKey);
  }

  async saveOHLCData(accountKey: string, data: OHLCData[]): Promise<void> {
    await this.init();
    return super.saveOHLCData(accountKey, data);
  }

  async deleteOHLCData(accountKey: string): Promise<void> {
    await this.init();
    return super.deleteOHLCData(accountKey);
  }

  async clearAccountData(
    companyName: string,
    serverName: string
  ): Promise<void> {
    await this.init();
    await super.clearAccountData(companyName, serverName);
    const { clearIdbAccountMarketData } = await import(
      "@/lib/storage/idb-ops"
    );
    await clearIdbAccountMarketData(companyName, serverName);
  }

  async clearSymbolData(
    companyName: string,
    serverName: string,
    symbol: string
  ): Promise<void> {
    await this.init();
    const key = generateAccountSymbolKey(companyName, serverName, symbol);
    await super.deleteOHLCData(key);
    const { clearIdbSymbolMarketData } = await import("@/lib/storage/idb-ops");
    await clearIdbSymbolMarketData(companyName, serverName, symbol);
  }
}

// Create enhanced singleton
const enhancedOHLCIndexDB = new EnhancedOHLCIndexDB();

/**
 * Load OHLC data from IndexedDB or fetch from server
 * @deprecated Hydration is handled by useChartStore3
 */
export const loadFromIndexDB = async (_symbol: string): Promise<void> => {
  console.warn(
    "[IndexDB] loadFromIndexDB is deprecated. Chart store hydrates from IndexedDB on symbol select."
  );
};

/**
 * Save OHLC rows to IndexedDB for an account+symbol key.
 */
export const saveToIndexDB = async (
  accountKey: string,
  data: OHLCData[]
): Promise<void> => {
  try {
    if (data.length === 0) return;
    await enhancedOHLCIndexDB.saveOHLCData(accountKey, data);
  } catch (error) {
    console.error(`[IndexDB] Error saving data for ${accountKey}:`, error);
  }
};

/**
 * Clear cached data for a specific account key
 */
export const clearIndexDBData = async (accountKey: string): Promise<void> => {
  try {
    await enhancedOHLCIndexDB.deleteOHLCData(accountKey);
  } catch (error) {
    console.error(`[IndexDB] Error clearing data for ${accountKey}:`, error);
  }
};

/**
 * Clear all cached data for an account
 */
export const clearAccountIndexDBData = async (
  companyName: string,
  serverName: string
): Promise<void> => {
  try {
    const { clearIdbAccountMarketData } = await import("@/lib/storage/idb-ops");
    await clearIdbAccountMarketData(companyName, serverName);
  } catch (error) {
    console.error(
      `[IndexDB] Error clearing account data for ${companyName}_${serverName}:`,
      error
    );
  }
};

export const clearSymbolIndexDBData = async (
  companyName: string,
  serverName: string,
  symbol: string
): Promise<void> => {
  try {
    await enhancedOHLCIndexDB.clearSymbolData(companyName, serverName, symbol);
  } catch (error) {
    console.error(
      `[IndexDB] Error clearing symbol data for ${companyName}_${serverName}_${symbol}:`,
      error
    );
  }
};

/**
 * Clear all cached data
 */
export const clearAllIndexDBData = async (): Promise<void> => {
  try {
    await enhancedOHLCIndexDB.init();
    await enhancedOHLCIndexDB.clearAll();
  } catch (error) {
    console.error("[IndexDB] Error clearing all data:", error);
  }
};

export default enhancedOHLCIndexDB;
